import type { FC, ReactNode } from 'react'
import type { Ajustes, CategoriaResumen } from '@/types'

import Link from 'next/link'
import { ArrowUpRight, Mail, MapPin, MessageCircle, Phone } from 'lucide-react'
import { cms } from '@/lib/cms'
import { mailHref, telHref, whatsappHref } from '@/lib/format'
import Container from '@/components/generals/container'
import Logo from '@/components/generals/logo'
import { services } from '@/constants/content'
import { mainNavigation, quoteHref } from '@/constants/site'

interface SiteFooterProps {
    ajustes: Ajustes
    categorias: Array<CategoriaResumen>
}

const columnTitleClass = 'text-[0.68rem] font-semibold uppercase tracking-[0.26em] text-brass'
const linkClass = 'text-sm text-ivory/75 transition-colors hover:text-brass'

const SiteFooter: FC<SiteFooterProps> = ({ ajustes, categorias }): ReactNode => {
    const email = mailHref(ajustes.correo)
    const phone = telHref(ajustes.telefono)
    const whatsapp = whatsappHref(ajustes.whatsapp, 'Hola, me gustaría recibir información sobre sus reconocimientos personalizados.')

    const socials = [
        { label: 'Instagram', href: ajustes.instagram_url },
        { label: 'Facebook', href: ajustes.facebook_url },
        { label: 'LinkedIn', href: ajustes.linkedin_url },
        { label: 'TikTok', href: ajustes.tiktok_url }
    ].filter((item): item is { label: string, href: string } => Boolean(item.href))

    return (
        <footer className='surface-grain relative border-t border-ivory/10 bg-black'>
            <Container className='grid gap-12 py-16 md:py-20 lg:grid-cols-12 lg:gap-10'>
                <div className='flex flex-col gap-6 lg:col-span-4'>
                    <Link href='/' aria-label='TROFÉA, ir al inicio' className='w-fit'>
                        <Logo />
                    </Link>

                    {ajustes.descripcion_marca && (
                        <p className='max-w-sm text-sm leading-relaxed text-muted-foreground'>
                            <span data-mdlfy-cms={cms('ajustes', ajustes.id, 'descripcion_marca')}>{ajustes.descripcion_marca}</span>
                        </p>
                    )}

                    {socials.length > 0 && (
                        <ul aria-label='Redes sociales' className='flex flex-wrap gap-x-5 gap-y-2'>
                            {socials.map(social => (
                                <li key={social.label}>
                                    <a
                                        href={social.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.18em] text-ivory/70 transition-colors hover:text-brass'
                                    >
                                        {social.label}
                                        <ArrowUpRight aria-hidden='true' className='size-3.5' />
                                        <span className='sr-only'>(se abre en una pestaña nueva)</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <nav aria-label='Navegación del pie de página' className='grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-5'>
                    <div className='flex flex-col gap-4'>
                        <p className={columnTitleClass}>Navegación</p>

                        <ul className='flex flex-col gap-3'>
                            {mainNavigation.map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} className={linkClass}>{item.label}</Link>
                                </li>
                            ))}

                            <li>
                                <Link href={quoteHref} className={linkClass}>Solicitar cotización</Link>
                            </li>
                        </ul>
                    </div>

                    <div className='flex flex-col gap-4'>
                        <p className={columnTitleClass}>Categorías</p>

                        <ul className='flex flex-col gap-3'>
                            {categorias.map(categoria => (
                                <li key={categoria.slug}>
                                    <Link href={`/catalogo/${categoria.slug}`} className={linkClass}>
                                        <span data-mdlfy-cms={cms('categorias', categoria.id, 'nombre')}>{categoria.nombre}</span>
                                    </Link>
                                </li>
                            ))}

                            <li>
                                <Link href='/catalogo' className={linkClass}>Catálogo completo</Link>
                            </li>
                        </ul>
                    </div>

                    <div className='col-span-2 flex flex-col gap-4 sm:col-span-1'>
                        <p className={columnTitleClass}>Servicios</p>

                        <ul className='flex flex-col gap-3'>
                            {services.map(service => (
                                <li key={service.id}>
                                    <Link href={`/servicios#${service.id}`} className={linkClass}>{service.title}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </nav>

                <div className='flex flex-col gap-4 lg:col-span-3'>
                    <p className={columnTitleClass}>Contacto</p>

                    <ul className='flex flex-col gap-4 text-sm'>
                        {ajustes.correo && (
                            <li className='flex items-start gap-3'>
                                <Mail aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-brass' />
                                {email ? (
                                    <a href={email} className={linkClass}>
                                        <span data-mdlfy-cms={cms('ajustes', ajustes.id, 'correo')}>{ajustes.correo}</span>
                                    </a>
                                ) : (
                                    <span className='text-ivory/75' data-mdlfy-cms={cms('ajustes', ajustes.id, 'correo')}>{ajustes.correo}</span>
                                )}
                            </li>
                        )}

                        {ajustes.telefono && (
                            <li className='flex items-start gap-3'>
                                <Phone aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-brass' />
                                {phone ? (
                                    <a href={phone} className={linkClass}>
                                        <span data-mdlfy-cms={cms('ajustes', ajustes.id, 'telefono')}>{ajustes.telefono}</span>
                                    </a>
                                ) : (
                                    <span className='text-ivory/75' data-mdlfy-cms={cms('ajustes', ajustes.id, 'telefono')}>{ajustes.telefono}</span>
                                )}
                            </li>
                        )}

                        {whatsapp && (
                            <li className='flex items-start gap-3'>
                                <MessageCircle aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-brass' />
                                <a href={whatsapp} target='_blank' rel='noopener noreferrer' className={linkClass}>
                                    WhatsApp <span data-mdlfy-cms={cms('ajustes', ajustes.id, 'whatsapp')}>{ajustes.whatsapp}</span>
                                    <span className='sr-only'> (se abre en una pestaña nueva)</span>
                                </a>
                            </li>
                        )}

                        {ajustes.ubicacion && (
                            <li className='flex items-start gap-3'>
                                <MapPin aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-brass' />
                                <span className='text-ivory/75' data-mdlfy-cms={cms('ajustes', ajustes.id, 'ubicacion')}>{ajustes.ubicacion}</span>
                            </li>
                        )}
                    </ul>
                </div>
            </Container>

            <div className='border-t border-ivory/10'>
                <Container className='flex flex-col gap-4 py-8 text-xs text-muted-foreground md:flex-row md:items-start md:justify-between md:gap-10'>
                    <div className='flex flex-col gap-2 md:max-w-2xl'>
                        <p>
                            <span suppressHydrationWarning>© {new Date().getFullYear()}</span> TROFÉA. Todos los derechos reservados.
                        </p>

                        {ajustes.aviso_demo && (
                            <p className='leading-relaxed text-ivory/50'>
                                <span data-mdlfy-cms={cms('ajustes', ajustes.id, 'aviso_demo')}>{ajustes.aviso_demo}</span>
                            </p>
                        )}
                    </div>

                    <ul className='flex shrink-0 flex-wrap gap-x-6 gap-y-2'>
                        <li>
                            <Link href='/privacidad' className='transition-colors hover:text-brass'>Aviso de privacidad</Link>
                        </li>
                        <li>
                            <Link href='/contacto' className='transition-colors hover:text-brass'>Contacto</Link>
                        </li>
                    </ul>
                </Container>
            </div>
        </footer>
    )
}

export default SiteFooter
